import { z } from 'zod'
import {
  addressSchema,
  optionalAddressSchema,
  dateSchema,
  phoneNumberSchema,
  getTaxIdValidationResult
} from 'utils/custom-schemas'
import { TaxNumberType } from 'generated/graphql'

const generalInformationBaseSchema = z.object({
  businessDetails: z.object({
    legalBusinessName: z.string().min(1),
    doingBusinessAs: z.string().min(1)
  }),
  businessStructureDetails: z.object({
    registeredBusinessAddress: addressSchema,
    businessPhoneNumber: phoneNumberSchema({
      message: 'invalidPhoneNumber'
    }),
    physicalBusinessAddress: optionalAddressSchema,
    sameAsRegisteredAddress: z.boolean().nullish(),
    dbaPhoneNumber: phoneNumberSchema({
      allowEmpty: true,
      message: 'invalidPhoneNumber'
    }),
    dateOfIncorporation: dateSchema(z.date()),
    businessStructure: z.string().min(1),
    businessEmail: z.string().email().min(1),
    publiclyTradedCorpExchange: z.string().nullish(),
    publiclyTradedCorporationSymbol: z
      .string()
      .transform(val => val.toUpperCase())
      .pipe(
        z.string().regex(/^[A-Z]{1,5}$/, { message: 'invalidTickerSymbol' })
      )
      .nullish()
  }),
  customerSupport: z.object({
    phone: phoneNumberSchema({
      allowEmpty: true,
      message: 'invalidPhoneNumber'
    })
  }),
  taxInfo: z.object({
    taxpayerName: z.string().min(1),
    taxpayerIdNumber: z.string().min(1),
    taxpayerIdType: z.string().min(1),
    address: optionalAddressSchema,
    sameAsRegisteredAddress: z.boolean().nullish(),
    exemptPayee: z.boolean(),
    taxExempt501c: z.boolean(),
    electronicTaxDocumentConsent: z.boolean()
  }),
  websites: z
    .array(
      z.object({
        domain: z.string(),
        login: z.string().nullish(),
        password: z.string().nullish(),
        platformRequiresTestUser: z.boolean().nullish()
      })
    )
    .min(1)
})

const generalInformationCrossFieldRefinements = (
  data: unknown,
  ctx: z.RefinementCtx
): void => {
  const root = (data ?? {}) as {
    businessStructureDetails?: Record<string, unknown>
    taxInfo?: {
      taxpayerIdNumber?: string | null
      taxpayerIdType?: string | null
      address?: unknown
      sameAsRegisteredAddress?: boolean | null
    } & Record<string, unknown>
  }

  if (root.businessStructureDetails) {
    const result = addressSchema.safeParse(
      root.businessStructureDetails.physicalBusinessAddress
    )
    if (
      !root.businessStructureDetails.sameAsRegisteredAddress &&
      !result.success
    ) {
      result.error.issues.forEach(issue => {
        ctx.addIssue({
          ...issue,
          path: [
            'businessStructureDetails',
            'physicalBusinessAddress',
            ...issue.path
          ]
        })
      })
    }
  }

  if (root.taxInfo) {
    if (!root.taxInfo.sameAsRegisteredAddress) {
      const result = addressSchema.safeParse(root.taxInfo.address)
      if (!result.success) {
        result.error.issues.forEach(issue => {
          ctx.addIssue({
            ...issue,
            path: ['taxInfo', 'address', ...issue.path]
          })
        })
      }
    }

    if (root.taxInfo.taxpayerIdType && !root.taxInfo.taxpayerIdNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'taxpayerIdNumberRequired',
        path: ['taxInfo', 'taxpayerIdNumber']
      })
    }

    if (root.taxInfo.taxpayerIdType && root.taxInfo.taxpayerIdNumber) {
      const result = getTaxIdValidationResult(
        root.taxInfo.taxpayerIdType as TaxNumberType,
        root.taxInfo.taxpayerIdNumber
      )

      if (!result.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.message ?? 'invalidTaxIdFormat',
          path: ['taxInfo', 'taxpayerIdNumber']
        })
      }
    }
  }
}

export const generalInformationSchema = z.intersection(
  z.any().superRefine(generalInformationCrossFieldRefinements),
  generalInformationBaseSchema
)

export type GeneralInformationForm = z.infer<
  typeof generalInformationBaseSchema
>
